import { useTranslation } from 'react-i18next';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import { MOBILE_TABLE_DENSITIES, TABLE_DENSITIES, TableDensity } from './density';
import styles from './DensityToggle.module.scss';

interface DensityToggleProps {
  value: TableDensity;
  onChange: (density: TableDensity) => void;
  showOnMobile?: boolean;
}

const LABEL_KEYS: Record<TableDensity, string> = {
  airy: 'densityAiry',
  normal: 'densityNormal',
  compact: 'densityCompact',
};

const DensityToggle = ({ value, onChange, showOnMobile = false }: DensityToggleProps) => {
  const { t } = useTranslation();
  const { isSidebarDocked } = useDeviceDetection();

  if (!isSidebarDocked && !showOnMobile) return null;

  const densities: readonly TableDensity[] = isSidebarDocked ? TABLE_DENSITIES : MOBILE_TABLE_DENSITIES;
  // The selected value can be `airy` when the sidebar has just collapsed to a
  // drawer; fall back to the first mobile option so the pressed state stays
  // consistent with what the user can pick from here.
  const selectedForRender: TableDensity = densities.includes(value) ? value : densities[0];

  return (
    <div className={`${styles.container} ${showOnMobile ? styles.mobileEnabled : ''}`}>
      <span className={`${styles.label} body-md-regular`}>{t('tableDensity')}:</span>
      <div className={styles.group} role="radiogroup" aria-label={t('tableDensity')} data-testid="density-toggle">
        {densities.map((density) => (
          <button
            key={density}
            type="button"
            role="radio"
            aria-checked={selectedForRender === density}
            aria-pressed={selectedForRender === density}
            tabIndex={selectedForRender === density ? 0 : -1}
            data-density-option={density}
            className={`${styles.option} ${selectedForRender === density ? styles.optionSelected : ''} body-sm-regular`}
            onClick={() => onChange(density)}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const currentIndex = Math.max(0, densities.indexOf(density));
              const rtl = typeof document !== 'undefined' && document.dir === 'rtl';
              // In RTL the visual "next" option sits to the left, so ArrowLeft
              // advances and ArrowRight goes back. Home / End always jump to the
              // logical first / last option in the list.
              const step = event.key === 'ArrowLeft' ? (rtl ? 1 : -1) : event.key === 'ArrowRight' ? (rtl ? -1 : 1) : 0;
              const nextIndex =
                event.key === 'Home' ? 0 : event.key === 'End' ? densities.length - 1 : (currentIndex + step + densities.length) % densities.length;
              const next = densities[nextIndex];
              onChange(next);
              event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-density-option="${next}"]`)?.focus();
            }}
          >
            {t(LABEL_KEYS[density])}
          </button>
        ))}
      </div>
    </div>
  );
};

export default DensityToggle;
